import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getHealthInsuranceList, createHealthInsurance, updateHealthInsurance, deleteHealthInsurance, getUsers, API_BASE_URL } from '../../model/api';
import Table from './common/Table.jsx';
import LoadingSpinner from './common/LoadingSpinner.jsx';
import RenewalRequestsTab from './RenewalRequestsTab.jsx';
import ExpiringPoliciesTab from './ExpiringPoliciesTab.jsx';
import { PlusCircleIcon, UploadIcon, RefreshIcon, EditIcon, TrashIcon } from './common/Icons.jsx';

const HealthInsuranceManager = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [partners, setPartners] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [activeTab, setActiveTab] = useState('policies');

    // Form State
    const [formData, setFormData] = useState({
        // policy_number removed (generated by backend)
        customer_name: '',
        customer_phone: '',
        customer_email: '', // Added
        partner_id: '',
        policy_type: '', // Renamed from plan_name
        premium_amount: '',
        commission_percentage: '', // Renamed from base_amount
        policy_start_date: new Date().toISOString().split('T')[0], // Default to today
        policy_term: 1,
        premium_by_year: [{ year: 1, premium_amount: '' }],
        status: 'active',
        document_pdf: null // Renamed from policy_document
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch Policies
            const params = { month: filterMonth };
            if (searchTerm) params.search = searchTerm;

            const policyResult = await getHealthInsuranceList(params);
            if (policyResult.ok) {
                setPolicies(policyResult.data || []);
            }

            // Fetch Partners (Keep this cached or fetch once if efficient, but for now fetch here)
            const userResult = await getUsers({ role: 'health_plus_partner' });
            if (Array.isArray(userResult)) {
                setPartners(userResult);
            } else if (userResult.users) {
                setPartners(userResult.users);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [filterMonth, searchTerm]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFormData(prev => ({ ...prev, document_pdf: e.target.files[0] }));
        }
    };

    const handleTermChange = (e) => {
        const term = parseInt(e.target.value);
        const premiums = [];
        for (let i = 1; i <= term; i++) {
            const existing = formData.premium_by_year?.find(p => p.year === i);
            premiums.push(existing || { year: i, premium_amount: '', commission_percentage: '' });
        }
        setFormData(prev => ({ ...prev, policy_term: term, premium_by_year: premiums }));
    };

    const handlePremiumChange = (year, field, value) => {
        setFormData(prev => {
            const newPremiums = prev.premium_by_year?.map(p => {
                if (p.year === year) {
                    return { ...p, [field]: value };
                }
                return p;
            });

            // Auto-fill commission for subsequent years if Year 1 is changed
            if (year === 1 && field === 'commission_percentage') {
                for (let i = 1; i < newPremiums.length; i++) {
                    if (!newPremiums[i].commission_percentage) {
                        newPremiums[i].commission_percentage = value;
                    }
                }
            }

            return { ...prev, premium_by_year: newPremiums };
        });
    };

    const [editingId, setEditingId] = useState(null);

    const handleEdit = (policy) => {
        setEditingId(policy._id);
        setFormData({
            partner_id: policy.partner_id?._id || policy.partner_id,
            customer_name: policy.customer_name,
            customer_phone: policy.customer_phone,
            customer_email: policy.customer_email,
            policy_type: policy.policy_type,
            premium_amount: policy.premium_amount,
            commission_percentage: policy.commission_percentage, // Legacy/Avg
            policy_start_date: policy.policy_start_date ? new Date(policy.policy_start_date).toISOString().split('T')[0] : '',
            policy_term: policy.policy_term_years || 1,
            premium_by_year: policy.premium_by_year && policy.premium_by_year.length > 0
                ? policy.premium_by_year
                : [{ year: 1, premium_amount: policy.premium_amount || '', commission_percentage: policy.commission_percentage || '' }],
            status: policy.status,
            document_pdf: null
        });
        setShowCreateForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this policy?')) return;
        try {
            const result = await deleteHealthInsurance(id);
            if (result.ok) {
                toast.success('Policy deleted');
                fetchData();
            } else {
                toast.error(result.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Delete error', error);
            toast.error('Error deleting policy');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);

        try {
            const submitData = new FormData();
            submitData.append('partner_id', formData.partner_id);
            submitData.append('customer_name', formData.customer_name);
            submitData.append('customer_phone', formData.customer_phone);
            submitData.append('customer_email', formData.customer_email);
            submitData.append('policy_type', formData.policy_type);

            // Calculate total premium from premium_by_year (for both single and multi-year)
            let totalPremium = 0;
            if (formData.premium_by_year && formData.premium_by_year.length > 0) {
                totalPremium = formData.premium_by_year.reduce((sum, p) => sum + Number(p.premium_amount || 0), 0);
            }
            submitData.append('premium_amount', totalPremium);

            // Deprecated global commission, but still sending for backward compatibility if needed, or explicitly 0
            // submitData.append('commission_percentage', formData.commission_percentage); 
            submitData.append('status', formData.status);

            // Multi-year fields
            submitData.append('policy_term_years', formData.policy_term);
            submitData.append('premium_by_year', JSON.stringify(formData.premium_by_year));

            // Calculate expires date based on start date and term
            if (formData.policy_start_date && formData.policy_term) {
                const start = new Date(formData.policy_start_date);
                const expires = new Date(start);
                expires.setFullYear(expires.getFullYear() + parseInt(formData.policy_term));
                submitData.append('policy_expires_date', expires.toISOString());
            }

            if (formData.document_pdf) {
                submitData.append('document_pdf', formData.document_pdf);
            }

            let result;
            if (editingId) {
                result = await updateHealthInsurance(editingId, submitData);
            } else {
                result = await createHealthInsurance(submitData);
            }

            if (result.ok) {
                toast.success(editingId ? 'Policy Updated' : 'Health Insurance Policy Created!');
                setShowCreateForm(false);
                setEditingId(null);
                setFormData({
                    customer_name: '',
                    customer_phone: '',
                    customer_email: '',
                    partner_id: '',
                    policy_type: '',
                    premium_amount: '',
                    commission_percentage: '',
                    policy_start_date: new Date().toISOString().split('T')[0],
                    policy_term: 1,
                    status: 'active',
                    premium_by_year: [{ year: 1, premium_amount: '', commission_percentage: '' }],
                    document_pdf: null
                });
                fetchData();
            } else {
                // Display detailed validation errors if available
                const errorMessage = result.error || result.message || 'Failed to save policy';
                toast.error(errorMessage, {
                    autoClose: 5000,
                    className: 'error-toast'
                });
                console.error('Backend validation error:', result);
            }
        } catch (error) {
            console.error('Save error:', error);
            // Extract error message from axios response
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Error saving policy';

            // If there are validation details, show them
            if (error.response?.data?.validationDetails) {
                const validationErrors = error.response.data.validationDetails
                    .map(v => v.message)
                    .join(', ');
                toast.error(`Validation Error: ${validationErrors}`, {
                    autoClose: 8000,
                    className: 'error-toast'
                });
            } else {
                toast.error(errorMessage, {
                    autoClose: 5000
                });
            }
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        { header: 'Policy No', accessor: 'policy_number' },
        {
            header: 'Customer', accessor: (row) => (
                <div>
                    <div className="font-medium text-gray-900">{row.customer_name}</div>
                    <div className="text-xs text-gray-500">{row.customer_phone}</div>
                </div>
            )
        },
        { header: 'Partner', accessor: (row) => row.partner_id?.name || 'N/A' },
        { header: 'Plan', accessor: 'plan_name' },
        {
            header: 'Premium',
            accessor: (row) => {
                const totalPremium = row.premium_amount || 0;
                const hasBreakdown = row.premium_by_year && row.premium_by_year.length > 0;

                return (
                    <div>
                        <div className="font-medium">Total: ₹{totalPremium.toLocaleString('en-IN')}</div>
                        {hasBreakdown && row.premium_by_year.map((p, idx) => (
                            <div key={idx} className="text-xs text-gray-500 mt-0.5">
                                Year {p.year}: ₹{Number(p.premium_amount).toLocaleString('en-IN')}
                            </div>
                        ))}
                    </div>
                );
            }
        },
        {
            header: 'Commission',
            accessor: (row) => {
                const totalComm = row.commission_amount || 0;
                const hasBreakdown = row.premium_by_year && row.premium_by_year.length > 0;
                const avgPercent = row.premium_amount ? ((totalComm / row.premium_amount) * 100).toFixed(1) : 0;

                return (
                    <div className="flex flex-col">
                        {hasBreakdown ? (
                            <div className="text-green-600 font-medium whitespace-nowrap">
                                Total: ₹{totalComm.toLocaleString('en-IN')}
                            </div>
                        ) : (
                            <span className="text-green-600 font-medium whitespace-nowrap">
                                ₹{totalComm.toLocaleString('en-IN') || 0}
                                <span className="text-xs text-gray-500 ml-1">({avgPercent}%)</span>
                            </span>
                        )}

                        {hasBreakdown && row.premium_by_year.map((p, idx) => {
                            const commAmt = p.commission_amount || (Number(p.premium_amount) * (Number(p.commission_percentage || 0) / 100));
                            return (
                                <div key={idx} className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                                    Y{p.year}: ₹{commAmt.toLocaleString('en-IN')} ({p.commission_percentage}%)
                                </div>
                            );
                        })}

                        {row.is_renewal && row.review_commission_amount > 0 && (
                            <span className="text-xs text-green-500 whitespace-nowrap mt-1">
                                + ₹{row.review_commission_amount?.toLocaleString('en-IN') || 0} (Bonus {row.review_commission_percentage}%)
                            </span>
                        )}
                    </div>
                );
            }
        },
        { header: 'Expiry', accessor: (row) => new Date(row.policy_expires_date).toLocaleDateString() },
        {
            header: 'Payout', accessor: (row) => (
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.payout_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {row.payout_status === 'paid' ? 'PAID' : 'PROCESSING'}
                </span>
            )
        },
        {
            header: 'Status', accessor: (row) => (
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.status === 'active' ? 'bg-green-100 text-green-800' :
                    row.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                    {row.status?.toUpperCase()}
                </span>
            )
        },
        {
            header: 'Actions', accessor: (row) => (
                <div className="flex space-x-2">
                    {row.document_pdf_url && (
                        <a href={row.document_pdf_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900" title="View Document">
                            Doc
                        </a>
                    )}
                    <button onClick={() => handleEdit(row)} className="text-blue-600 hover:text-blue-900" title="Edit">
                        <EditIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(row._id)} className="text-red-600 hover:text-red-900" title="Delete">
                        <TrashIcon className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('policies')}
                            className={`${activeTab === 'policies'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            All Policies
                        </button>
                        <button
                            onClick={() => setActiveTab('renewals')}
                            className={`${activeTab === 'renewals'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Review Policies
                            <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                                Request
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('expiring')}
                            className={`${activeTab === 'expiring'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Expiring Soon
                            <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                                30 Days
                            </span>
                        </button>
                    </nav>

                    {activeTab === 'policies' && (
                        <button
                            onClick={() => {
                                setEditingId(null);
                                setFormData({
                                    customer_name: '',
                                    customer_phone: '',
                                    customer_email: '',
                                    partner_id: '',
                                    policy_type: '',
                                    premium_amount: '',
                                    commission_percentage: '',
                                    policy_start_date: new Date().toISOString().split('T')[0],
                                    policy_term: 1,
                                    premium_by_year: [{ year: 1, premium_amount: '', commission_percentage: '' }],
                                    status: 'active',
                                    document_pdf: null
                                });
                                setShowCreateForm(true);
                            }}
                            className="bg-indigo-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-indigo-700"
                        >
                            <PlusCircleIcon className="w-5 h-5" /> Add Health Policy
                        </button>
                    )}
                </div>

                {activeTab === 'policies' && (
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                        <input
                            type="month"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            className="border rounded px-3 py-2"
                        />
                        <div className="flex-1 w-full">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border rounded px-3 py-2"
                            />
                        </div>
                        <button onClick={fetchData} className="p-2 text-gray-600 hover:text-gray-900"><RefreshIcon className="w-5 h-5" /></button>
                    </div>
                )}
            </div>

            {activeTab === 'renewals' ? (
                <RenewalRequestsTab />
            ) : activeTab === 'expiring' ? (
                <ExpiringPoliciesTab />
            ) : (
                loading ? <LoadingSpinner /> : <Table columns={columns} data={policies} />
            )}

            {/* Create Modal */}
            {showCreateForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit' : 'Create New'} Health Insurance Policy</h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Partner *</label>
                                    <select name="partner_id" required value={formData.partner_id} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2">
                                        <option value="">Select Partner</option>
                                        {partners.map(p => <option key={p._id} value={p._id}>{p.name} ({p.role})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Plan Name (Type) *</label>
                                    <input name="policy_type" required value={formData.policy_type} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2" placeholder="e.g. Family Floater" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                                    <input name="customer_name" required value={formData.customer_name} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Customer Email *</label>
                                    <input type="email" name="customer_email" required value={formData.customer_email} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Customer Phone *</label>
                                    <input name="customer_phone" required value={formData.customer_phone} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                                    <input type="date" name="policy_start_date" required value={formData.policy_start_date} onChange={handleInputChange} className="mt-1 block w-full border rounded px-3 py-2" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Term (Years)</label>
                                    <select name="policy_term" value={formData.policy_term} onChange={handleTermChange} className="mt-1 block w-full border rounded px-3 py-2">
                                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Year{n > 1 ? 's' : ''}</option>)}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Premium Breakdown</label>
                                    <div className="space-y-2 bg-gray-50 p-3 rounded-md border border-gray-200">
                                        {formData.premium_by_year?.map((p) => (
                                            <div key={p.year} className="flex flex-col sm:flex-row gap-3">
                                                <div className="flex items-center gap-2 flex-1">
                                                    <span className="w-16 text-sm font-medium text-gray-600">Year {p.year}:</span>
                                                    <div className="relative flex-1">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">₹</span>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            value={p.premium_amount}
                                                            onChange={(e) => handlePremiumChange(p.year, 'premium_amount', e.target.value)}
                                                            className="w-full pl-6 border rounded px-2 py-1 text-sm"
                                                            placeholder="Premium"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-1">
                                                    <div className="relative flex-1">
                                                        <input
                                                            type="number"
                                                            value={p.commission_percentage}
                                                            onChange={(e) => handlePremiumChange(p.year, 'commission_percentage', e.target.value)}
                                                            className="w-full border rounded px-2 py-1 text-sm"
                                                            placeholder="Prem %"
                                                            required
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-500 whitespace-nowrap">Commission %</span>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-between items-center pt-2 border-t mt-2">
                                            <span className="text-sm font-semibold text-gray-700">Total Premium:</span>
                                            <span className="font-bold text-gray-900">
                                                ₹{(formData.premium_by_year || []).reduce((sum, p) => sum + Number(p.premium_amount || 0), 0).toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Commission input removed as it is now per year */}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Policy Document (PDF)</label>
                                <input type="file" accept=".pdf" onChange={handleFileChange} className="mt-1 block w-full" />
                                <p className="text-xs text-gray-500">Upload the policy PDF.</p>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                                <button type="submit" disabled={creating} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                                    {creating ? 'Saving...' : (editingId ? 'Update Policy' : 'Create Policy')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthInsuranceManager;
